import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import {
  dismissCurrentShepherdStep,
  ensureShepherdModalOverlayHidden,
  exitShepherdLocationTypingFreeze,
  isShepherdTourStepSpotlightEligible,
  registerInteractiveTourGuards,
} from '../shepherd-interactive-step';
import {
  registerTourHighlightedFieldLockGuards,
  releaseAllShepherdTourFieldLocks,
  SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
  withTourFieldLockTipMessage,
} from '../shepherd-tour-field-lock';
import {
  clearExploreSearchTourUi,
  EXPLORE_SEARCH_WAIT_STEP_ID,
  resolveExploreSearchTarget,
  setExploreSearchInteractionActive,
  setExploreSearchSpotlightActive,
} from '../explore-search.shepherd';

export { EXPLORE_SEARCH_WAIT_STEP_ID } from '../explore-search.shepherd';
import { waitForElement } from '../shepherd.utils';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const EXPLORE_ACTIVITY_TOUR = 'explore-activity';

export const EXPLORE_ACTIVITY_CONTINUATION_TOUR = 'explore-activity-continuation';

export const EXPLORE_JOIN_REQUEST_STEP_ID = 'exp-join-request';

function exploreHasNoResultsEmpty(): boolean {
  return !!document.querySelector('[data-tour="explore-no-results"]');
}

/** Filter header button exists whenever search ran; card grid may still be loading. */
function exploreFilterTriggerInDom(): boolean {
  return !!document.querySelector('[data-tour="explore-filter-trigger"]');
}

function appendExploreActivityPostSearchSteps(tour: ShepherdTour, fromContinuation: boolean): void {
  const stepButtons = fromContinuation ? [TOUR_BUTTONS.next] : [TOUR_BUTTONS.back, TOUR_BUTTONS.next];

  tour.addStep({
    id: 'exp-filter-modal',
    title: 'Filter modal',
    text: 'Filter by skill level, date, activity type, or location.',
    attachTo: { element: '[data-tour="explore-filter-trigger"]', on: 'bottom' },
    buttons: stepButtons,
    // Card grid appears after load; filter button exists as soon as results section renders (avoids skipping this step if OK clicked during skeleton).
    showOn: () => exploreFilterTriggerInDom(),
    beforeShowPromise() {
      return waitForElement('[data-tour="explore-filter-trigger"]', 4000).then(() => undefined);
    },
  });

  // tour.addStep({
  //   id: 'exp-results',
  //   title: 'Results area',
  //   text: 'Activity cards show host details, course, skill level, and available spots.',
  //   attachTo: { element: '[data-tour="explore-results-panel"]', on: 'top' },
  //   buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.next],
  //   showOn: () => !!document.querySelector('[data-tour="explore-activity-cards"]'),
  //   beforeShowPromise() {
  //     return waitForElement('[data-tour="explore-results-panel"]', 4000).then(() => undefined);
  //   },
  // });

  tour.addStep({
    id: EXPLORE_JOIN_REQUEST_STEP_ID,
    title: 'Request to join',
    text: 'Request to join activities that match your golf style.',
    attachTo: { element: '[data-tour="explore-join-request"]', on: 'top' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.finish],
    showOn: () => !!document.querySelector('[data-tour="explore-join-request"]'),
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement('[data-tour="explore-join-request"]', 4000).then(() => undefined);
    },
  });

  tour.addStep({
    id: 'exp-no-results',
    title: 'No nearby activities.',
    text: 'No golf activities nearby yet. Be the first golfer to organize one.',
    attachTo: { element: '[data-tour="explore-no-results"]', on: 'top' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.finish],
    showOn: () => exploreHasNoResultsEmpty(),
    beforeShowPromise() {
      return waitForElement('[data-tour="explore-no-results"]', 4000).then(() => undefined);
    },
  });
}

function unlockExploreSearchEditableFields(): void {
  const root = resolveExploreSearchTarget();
  if (!root) {
    return;
  }
  root.querySelectorAll('input, textarea').forEach((node) => {
    if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
      node.readOnly = false;
      node.removeAttribute('readonly');
      node.removeAttribute('data-shepherd-lock-stored');
      node.removeAttribute('data-shepherd-was-readonly');
    }
  });
  root.classList.remove('shepherd-tour-field-lock-target');
}

function clearExploreSearchStepOverlay(): void {
  exitShepherdLocationTypingFreeze(resolveExploreSearchTarget);
  clearExploreSearchTourUi();
  releaseAllShepherdTourFieldLocks();
  unlockExploreSearchEditableFields();
  ensureShepherdModalOverlayHidden();
}

function syncExploreSearchTourSpotlight(tour: ShepherdTour): void {
  if (isShepherdTourStepSpotlightEligible(tour, EXPLORE_SEARCH_WAIT_STEP_ID)) {
    setExploreSearchSpotlightActive(true);
    return;
  }
  setExploreSearchSpotlightActive(false);
}

/** Close search step tip only; later explore steps remain in the tour. */
const EXPLORE_SEARCH_STEP_OK = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    clearExploreSearchStepOverlay();
    dismissCurrentShepherdStep(this);
  },
};

export function buildExploreActivityTour(tour: ShepherdTour): void {
  registerInteractiveTourGuards(tour);
  registerTourHighlightedFieldLockGuards(tour, [EXPLORE_SEARCH_WAIT_STEP_ID], resolveExploreSearchTarget);

  tour.on('start', () => {
    queueMicrotask(() => syncExploreSearchTourSpotlight(tour));
    requestAnimationFrame(() => syncExploreSearchTourSpotlight(tour));
  });

  tour.on('show', (ev: { step?: { id?: string } }) => {
    if (ev?.step?.id === EXPLORE_SEARCH_WAIT_STEP_ID) {
      syncExploreSearchTourSpotlight(tour);
      queueMicrotask(() => syncExploreSearchTourSpotlight(tour));
      requestAnimationFrame(() => syncExploreSearchTourSpotlight(tour));
      return;
    }
    if (ev?.step?.id) {
      clearExploreSearchTourUi();
    }
  });

  tour.on('hide', (ev: { step?: { id?: string } }) => {
    if (ev?.step?.id === EXPLORE_SEARCH_WAIT_STEP_ID) {
      clearExploreSearchStepOverlay();
    }
  });

  tour.addStep({
    id: EXPLORE_SEARCH_WAIT_STEP_ID,
    title: 'Search',
    text: withTourFieldLockTipMessage('Search by course, city, or golf activity.'),
    attachTo: { element: '[data-tour="explore-search"]', on: 'bottom' },
    buttons: [EXPLORE_SEARCH_STEP_OK],
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    beforeShowPromise() {
      return waitForElement('[data-tour="explore-search"]', 2500).then(() => undefined);
    },
  });

  appendExploreActivityPostSearchSteps(tour, false);
}

export function buildExploreActivityContinuationTour(tour: ShepherdTour): void {
  appendExploreActivityPostSearchSteps(tour, true);
}
